/**
 * Frontend runtime configuration. All values come from Vite-injected
 * `import.meta.env.VITE_*` variables, validated once at module-load time.
 *
 * Phase 2 wires the Medicus IAM OIDC + PKCE flow per ADR-0021. The IAM
 * `authority`/end-session URLs and the public OIDC `client_id` are
 * deployment-specific; the redirect URI is the SPA's `/auth/callback`.
 *
 * `VITE_API_BASE_URL` is the backend (onboarding-api) origin — same-origin
 * in production (backend-served SPA per ADR-0041 Q3) but typically
 * `http://localhost:8000` in dev. The token-exchange endpoint
 * (`POST /api/v1/auth/exchange`) lives there; the frontend NEVER holds
 * the IAM client secret (per readiness Q-2 IAM contract).
 */

/**
 * Runtime config injected by `GET /config.js` (see onboarding-api
 * `api/frontend.py`). Vite bakes `import.meta.env.VITE_*` at BUILD time, which
 * would make the container image environment-specific — the same digest could
 * not be promoted from alpha to prod, which is what ADR-0025's tagging
 * convention exists to allow. So the deployed bundle carries no environment and
 * reads it from the server instead.
 *
 * Precedence: runtime `window.__SR_CONFIG__` → build-time `VITE_*` → dev
 * fallback. `vite dev` and vitest see no runtime config and behave exactly as
 * they did before.
 */
declare global {
  interface Window {
    __SR_CONFIG__?: Partial<Record<string, string | boolean>>;
  }
}

function readRuntime(key: string): string | undefined {
  if (typeof window === "undefined") return undefined;
  const value = window.__SR_CONFIG__?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function readEnv(key: string, fallback?: string): string {
  // Vite replaces `import.meta.env.VITE_*` at build time. In test (vitest +
  // jsdom) the values are undefined unless seeded by the test harness;
  // fallbacks keep import-time validation from blowing up the test runner.
  const value = (import.meta.env as Record<string, string | undefined>)[key];
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  if (fallback !== undefined) {
    return fallback;
  }
  throw new Error(
    `Missing required environment variable: ${key}. Set it in .env (see .env.example).`,
  );
}

export interface AppConfig {
  /** OIDC authorize endpoint — e.g. `https://iam.alpha.medicus.ai/connect/authorize`. */
  iamAuthorizeUrl: string;
  /**
   * OIDC end-session endpoint, e.g. `https://iam.alpha.medicus.ai/connect/logout`. The BFF
   * reads it from IAM's discovery document (docs#239 note 19795 item 42).
   */
  iamEndSessionUrl: string;
  /** Public OIDC client_id registered with IAM for this SPA. */
  iamClientId: string;
  /** Browser callback URL (post-IAM redirect). */
  iamRedirectUri: string;
  /** Space-separated OIDC scope string; must include `openid`. */
  iamScope: string;
  /** Backend base URL (onboarding-api). */
  apiBaseUrl: string;
  /**
   * Gate B M1 S7. True only when onboarding-api runs with SR_PORTAL_REPORTS_ENABLED, which
   * is the only time its /api/v1/portal/reports routes exist. Off, the Reports page and
   * its links are not rendered at all. Not an access control: the BFF refuses anyone
   * without the clinical read permission whatever this says.
   */
  portalReportsEnabled: boolean;
  /**
   * docs#239 note 17369 item 4. True only when onboarding-api runs with
   * SR_HELD_MESSAGE_REVIEW_ENABLED, the only time GET /api/v1/portal/lis/messages exists.
   * Off, the Lab messages button and page are not rendered, as with the Reports page,
   * rather than a page that loads into a raw 404. Not an access control.
   */
  heldMessageReviewEnabled: boolean;
  /**
   * Gate B M2 S11. True only when onboarding-api runs with SR_PORTAL_REPORT_APPROVAL_ENABLED,
   * the only time POST /api/v1/portal/reports/{id}/approve and /withdraw exist. Off, the
   * review view shows no approve or withdraw button. Not an access control either.
   */
  portalReportApprovalEnabled: boolean;
  /**
   * Gate B M2 S16. True only when onboarding-api runs with
   * SR_PORTAL_REPORT_ATTACHMENTS_ENABLED, the only time its report attachment routes exist.
   * Off, the review view shows no attachment section and asks for none.
   */
  portalReportAttachmentsEnabled: boolean;
  /**
   * Gate B M3 S21. True only when onboarding-api runs with SR_PORTAL_PIN_REISSUE_ENABLED,
   * the only time POST /api/v1/portal/reports/{id}/pin-reissue exists. Off, a released
   * report shows no "Send a new PIN" button. Not an access control either.
   */
  portalPinReissueEnabled: boolean;
  /**
   * Gate B white-labelling. True only when onboarding-api runs with SR_BRANDING_ENABLED, the
   * only time /api/v1/branding exists. Off, no Branding or My signature page is registered or
   * linked, and the portal keeps the Medicus look for everyone. Not an access control: the
   * BFF and the orchestrator decide who may brand what.
   */
  brandingEnabled: boolean;
}

const DEV_FALLBACK_AUTHORIZE = "http://localhost:8081/connect/authorize";
const DEV_FALLBACK_ENDSESSION = "http://localhost:8081/connect/logout";
const DEV_FALLBACK_CLIENT_ID = "smart-reports-onboarding-web";
const DEV_FALLBACK_REDIRECT_URI =
  typeof window !== "undefined"
    ? `${window.location.origin}/auth/callback`
    : "http://localhost:5173/auth/callback";
const DEV_FALLBACK_SCOPE = "openid profile email catalog:read catalog:author";
const DEV_FALLBACK_API_BASE_URL = "http://localhost:8000";

/** Runtime value if the server supplied one, else the build-time/dev chain. */
function resolve(runtimeKey: string, envKey: string, fallback: string): string {
  return readRuntime(runtimeKey) ?? readEnv(envKey, fallback);
}

export const config: AppConfig = {
  iamAuthorizeUrl: resolve("iamAuthorizeUrl", "VITE_IAM_AUTHORIZE_URL", DEV_FALLBACK_AUTHORIZE),
  iamEndSessionUrl: resolve(
    "iamEndSessionUrl",
    "VITE_IAM_END_SESSION_URL",
    DEV_FALLBACK_ENDSESSION,
  ),
  iamClientId: resolve("iamClientId", "VITE_IAM_CLIENT_ID", DEV_FALLBACK_CLIENT_ID),
  // Always the browser's own origin — the server cannot know the ingress host,
  // and IAM matches this against the registered redirect URI exactly.
  iamRedirectUri: readEnv("VITE_IAM_REDIRECT_URI", DEV_FALLBACK_REDIRECT_URI),
  iamScope: resolve("iamScope", "VITE_IAM_SCOPE", DEV_FALLBACK_SCOPE),
  // `apiBaseUrl: ""` from /config.js is intentional and must survive: the SPA is
  // same-origin with the API, so a relative base is correct. `resolve` would
  // discard it as empty, so read it explicitly.
  apiBaseUrl:
    typeof window !== "undefined" && typeof window.__SR_CONFIG__?.apiBaseUrl === "string"
      ? window.__SR_CONFIG__.apiBaseUrl
      : readEnv("VITE_API_BASE_URL", DEV_FALLBACK_API_BASE_URL),
  // /config.js sends a JSON boolean, and only when the flag is on (absent means off), so
  // `readRuntime`, which takes strings, cannot read it. Anything but literal `true` is off.
  portalReportsEnabled:
    (typeof window !== "undefined" && window.__SR_CONFIG__?.portalReportsEnabled === true) ||
    readEnv("VITE_PORTAL_REPORTS_ENABLED", "false") === "true",
  // Same rule: a JSON boolean, present only when SR_HELD_MESSAGE_REVIEW_ENABLED is on.
  heldMessageReviewEnabled:
    (typeof window !== "undefined" && window.__SR_CONFIG__?.heldMessageReviewEnabled === true) ||
    readEnv("VITE_HELD_MESSAGE_REVIEW_ENABLED", "false") === "true",
  portalReportApprovalEnabled:
    (typeof window !== "undefined" && window.__SR_CONFIG__?.portalReportApprovalEnabled === true) ||
    readEnv("VITE_PORTAL_REPORT_APPROVAL_ENABLED", "false") === "true",
  // Same rule: a JSON boolean, present only when SR_PORTAL_PIN_REISSUE_ENABLED is on.
  portalPinReissueEnabled:
    (typeof window !== "undefined" && window.__SR_CONFIG__?.portalPinReissueEnabled === true) ||
    readEnv("VITE_PORTAL_PIN_REISSUE_ENABLED", "false") === "true",
  // Same rule as the line above: a JSON boolean, present only when on.
  portalReportAttachmentsEnabled:
    (typeof window !== "undefined" &&
      window.__SR_CONFIG__?.portalReportAttachmentsEnabled === true) ||
    readEnv("VITE_PORTAL_REPORT_ATTACHMENTS_ENABLED", "false") === "true",
  // Same rule: a JSON boolean, present only when SR_BRANDING_ENABLED is on.
  brandingEnabled:
    (typeof window !== "undefined" && window.__SR_CONFIG__?.brandingEnabled === true) ||
    readEnv("VITE_BRANDING_ENABLED", "false") === "true",
};
